import type { Tables } from "@/types/database";
import type { EventStatus } from "@/types/database";

type Event = Tables<"events"> & {
  quote?: Tables<"quotes"> | null;
  technical_sheet?: Tables<"technical_sheets"> | null;
};

type Payment = Tables<"payments">;

export function hasQuote(event: Event): boolean {
  return event.quote != null && event.quote.total_amount > 0;
}

export function hasTechnicalSheet(event: Event): boolean {
  return event.technical_sheet != null;
}

export function hasSignature(event: Event): boolean {
  return event.quote?.signature_date != null;
}

export function hasDepositPaid(payments: Payment[]): boolean {
  return payments.some(
    (p) => p.payment_type === "Deposit" && p.is_paid === true,
  );
}

export function isEventValidated(event: Event, payments: Payment[]): boolean {
  return hasSignature(event) && hasDepositPaid(payments);
}

export function canSendDocuments(event: Event): boolean {
  return hasQuote(event) && hasTechnicalSheet(event);
}

export function getValidationStatus(event: Event, payments: Payment[]) {
  const signatureReceived = hasSignature(event);
  const depositPaid = hasDepositPaid(payments);
  const depositAmount =
    payments.find((p) => p.payment_type === "Deposit")?.amount ?? 0;

  return {
    signatureReceived,
    signatureDate: event.quote?.signature_date ?? null,
    depositPaid,
    depositAmount,
    isValidated: signatureReceived && depositPaid,
  };
}

export function getNextStatus(
  currentStatus: EventStatus,
  event: Event,
  payments: Payment[],
): EventStatus | null {
  switch (currentStatus) {
    case "Project":
      return "First Contact";

    case "First Contact":
      if (canSendDocuments(event)) {
        return "Documents Sent";
      }
      return null;

    case "Documents Sent":
      if (isEventValidated(event, payments)) {
        return "Validated";
      }
      return null;

    case "Validated":
      return "Final Organization";

    case "Final Organization":
      return "Completed";

    case "Completed":
    case "Abandoned":
      return null;

    default:
      return null;
  }
}

export function canTransitionTo(
  from: EventStatus,
  to: EventStatus,
  event: Event,
  payments: Payment[],
): boolean {
  if (to === "Abandoned") {
    return from !== "Completed" && from !== "Abandoned";
  }

  const allowedTransitions: Record<EventStatus, EventStatus[]> = {
    Project: ["First Contact", "Abandoned"],
    "First Contact": ["Documents Sent", "Abandoned"],
    "Documents Sent": ["Validated", "Abandoned"],
    Validated: ["Final Organization", "Abandoned"],
    "Final Organization": ["Completed", "Abandoned"],
    Completed: [],
    Abandoned: [],
  };

  if (!allowedTransitions[from].includes(to)) {
    return false;
  }

  if (from === "First Contact" && to === "Documents Sent") {
    return canSendDocuments(event);
  }

  if (from === "Documents Sent" && to === "Validated") {
    return isEventValidated(event, payments);
  }

  return true;
}

export function getDocumentsSentRequirements(event: Event) {
  return {
    hasQuote: hasQuote(event),
    hasTechnicalSheet: hasTechnicalSheet(event),
    canSend: canSendDocuments(event),
  };
}

export function getValidatedRequirements(event: Event, payments: Payment[]) {
  return {
    hasSignature: hasSignature(event),
    hasDepositPaid: hasDepositPaid(payments),
    canValidate: isEventValidated(event, payments),
  };
}
